import { Component, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { erro: Error | null; info: string; recarregando: boolean }

// Erro tipico quando o index.html em cache pede um chunk lazy-loaded com hash antigo
// que ja sumiu do servidor depois de um deploy novo. Solucao: reload do index.html
// pra puxar os hashes atuais.
const RX_CHUNK_ERR = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|ChunkLoadError/i;
const CHUNK_RELOAD_KEY = 'chunk-reload-ts';
const CHUNK_RELOAD_COOLDOWN_MS = 10000;

function isChunkLoadError(err: Error): boolean {
  return RX_CHUNK_ERR.test(err.message || '');
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { erro: null, info: '', recarregando: false };

  static getDerivedStateFromError(erro: Error): State {
    if (isChunkLoadError(erro)) {
      const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
      if (Date.now() - last > CHUNK_RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        // Pequeno delay pra a tela "recarregando" pintar antes do reload sumir com tudo.
        setTimeout(() => window.location.reload(), 50);
        return { erro: null, info: '', recarregando: true };
      }
      // Cooldown ativo — ja recarregou ha pouco e bateu de novo. Mostra erro normal.
    }
    return { erro, info: '', recarregando: false };
  }

  override componentDidCatch(erro: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary]', erro, info);
    if (!isChunkLoadError(erro)) this.setState({ info: info.componentStack });
  }

  override render() {
    if (this.state.recarregando) {
      return (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
          <p style={{ fontSize: 16, opacity: 0.8 }}>Sistema foi atualizado. Recarregando...</p>
        </div>
      );
    }
    if (!this.state.erro) return this.props.children;
    return (
      <div style={{ padding: 24, maxWidth: 900, margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
        <h1 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 12, color: '#dc2626' }}>
          Erro na tela
        </h1>
        <p style={{ fontSize: 14, marginBottom: 16, opacity: 0.7 }}>
          A tela quebrou no meio da execucao. Tira print disso e me manda — abaixo tem a mensagem
          do erro e onde aconteceu, dou conserto rapido.
        </p>
        <div style={{ background: '#fee2e2', color: '#7f1d1d', padding: 12, borderRadius: 8, marginBottom: 12, fontFamily: 'monospace', fontSize: 13 }}>
          <strong>{this.state.erro.name}:</strong> {this.state.erro.message}
        </div>
        {this.state.erro.stack && (
          <details open style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 'bold', marginBottom: 6 }}>Stack trace</summary>
            <pre style={{ background: '#f3f4f6', padding: 12, borderRadius: 8, fontSize: 11, overflow: 'auto', maxHeight: 300 }}>
              {this.state.erro.stack}
            </pre>
          </details>
        )}
        {this.state.info && (
          <details>
            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 'bold', marginBottom: 6 }}>Componente</summary>
            <pre style={{ background: '#f3f4f6', padding: 12, borderRadius: 8, fontSize: 11, overflow: 'auto', maxHeight: 200 }}>
              {this.state.info}
            </pre>
          </details>
        )}
        <button
          onClick={() => { this.setState({ erro: null, info: '' }); window.location.reload(); }}
          style={{ marginTop: 16, padding: '8px 16px', background: 'var(--primary, #b45309)', color: '#fff', borderRadius: 6, border: 'none', cursor: 'pointer' }}
        >
          Recarregar pagina
        </button>
      </div>
    );
  }
}
